import { createToken, EmbeddedActionsParser, Lexer } from 'chevrotain';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import { has, head, identity, isArray, isNumber } from 'lodash-es';
import { match, P } from 'ts-pattern';
import { emptyStringToNull, isNonEmptyString } from './index.js';
import { inConstArr, invert } from './seq.js';
dayjs.extend(customParseFormat);
const Integer = createToken({ name: 'Integer', pattern: /\d+/ });
const FloatingPoint = createToken({
    name: 'FloatingPoint',
    pattern: /\d+\.\d+/,
});
const Identifier = createToken({
    name: 'Identifier',
    pattern: /[a-zA-Z0-9-]+/,
});
const StringChars = createToken({
    name: 'StringChars',
    pattern: /[^"\\{]+/,
});
const FactedStringFields = [
    'type',
    'genre',
    'rating',
    'actor',
    'director',
    'writer',
    'studio',
    'video_codec',
    'audio_codec',
    'tags',
    'show_tags',
    'show_studio',
    'show_genre',
    'audio_language',
    'subtitle_language',
    'video_dynamic_range',
    'media_source_name',
    'library_name',
];
const StringFields = [
    ...FactedStringFields,
    'library_id',
    'title',
    'show_title',
];
const StringField = createToken({
    name: 'StringField',
    pattern: new RegExp(StringFields.join('|')),
    longer_alt: Identifier,
});
const DateFields = ['release_date'];
const DateField = createToken({
    name: 'DateField',
    pattern: new RegExp(DateFields.join('|')),
    longer_alt: Identifier,
});
const NumericFields = [
    'duration',
    'minutes',
    'seconds',
    'video_bit_depth',
    'video_height',
    'video_width',
    'audio_channels',
    'release_year',
    'year',
];
const NumericField = createToken({
    name: 'NumericField',
    pattern: new RegExp(NumericFields.join('|')),
    longer_alt: Identifier,
});
const WhiteSpace = createToken({
    name: 'WhiteSpace',
    pattern: /\s+/,
    group: Lexer.SKIPPED,
});
const Comma = createToken({
    name: 'Comma',
    pattern: /,/,
});
const CombineAnd = createToken({
    name: 'CombineAnd',
    pattern: /AND/i,
    longer_alt: Identifier,
});
const CombineOr = createToken({
    name: 'CombineOr',
    pattern: /OR/i,
    longer_alt: Identifier,
});
const OpenParenGroup = createToken({
    name: 'OpenParenGroup',
    pattern: /\(/,
});
const CloseParenGroup = createToken({
    name: 'CloseParenGroup',
    pattern: /\)/,
});
const OpenArray = createToken({
    name: 'OpenArray',
    pattern: /\[/,
});
const CloseArray = createToken({
    name: 'CloseArray',
    pattern: /]/,
});
const OpenQuote = createToken({
    name: 'Quote',
    pattern: /"/,
    push_mode: 'stringMode',
});
const CloseQuote = createToken({
    name: 'Quote',
    pattern: /"/,
    pop_mode: true,
});
const EqOperator = createToken({ name: 'EqOperator', pattern: /:|=/ });
const NeqOperator = createToken({ name: 'NeqOperator', pattern: /!=/ });
const NotContainsOperator = createToken({
    name: 'NotContainsOperator',
    pattern: /!~/,
});
const ContainsOperator = createToken({
    name: 'ContainsOperator',
    pattern: /~/,
});
const LessThanOrEqualOperator = createToken({
    name: 'LTEOperator',
    pattern: /<=/,
});
const LessThanOperator = createToken({ name: 'LTOperator', pattern: /</ });
const GreaterThanOrEqualOperator = createToken({
    name: 'GTEOperator',
    pattern: />=/,
});
const GreaterThanOperator = createToken({ name: 'GTOperator', pattern: />/ });
const NotOperator = createToken({ name: 'NotOperator', pattern: /not/i });
const InOperator = createToken({ name: 'InOperator', pattern: /in/i });
const BetweenOperator = createToken({
    name: 'BetweenOperator',
    pattern: /between/i,
});
const allTokens = [
    WhiteSpace,
    Comma,
    OpenArray,
    CloseArray,
    OpenParenGroup,
    CloseParenGroup,
    OpenQuote,
    CombineAnd,
    CombineOr,
    LessThanOrEqualOperator,
    GreaterThanOrEqualOperator,
    EqOperator,
    NeqOperator,
    LessThanOperator,
    GreaterThanOperator,
    NotOperator,
    InOperator,
    BetweenOperator,
    NotContainsOperator,
    ContainsOperator,
    // Order matters here. float is more specific
    // than int.
    FloatingPoint,
    Integer,
    // Fields
    StringField,
    DateField,
    NumericField,
    // Catch all
    Identifier,
];
const SearchExpressionLexer = new Lexer({
    modes: {
        stringMode: [WhiteSpace, CloseQuote, StringChars],
        normalMode: allTokens,
    },
    defaultMode: 'normalMode',
});
const StringOps = ['=', '!=', '<', '<=', 'in', 'not in', 'contains', 'not contains'];
const NumericOps = ['=', '!=', '<', '<=', '>', '>=', 'between'];
const DateOps = ['=', '<', '<=', '>', '>=', 'between'];
const StringOpToApiType = {
    '<': 'starts with',
    '<=': 'starts with',
    '!=': '!=',
    '=': '=',
    contains: 'contains',
    'not contains': 'not contains',
    in: 'in',
    'not in': 'not in',
};
const NumericOpToApiType = {
    '!=': '!=',
    '<': '<',
    '<=': '<=',
    '=': '=',
    '>': '>',
    '>=': '>=',
    // This depends on inclusivity
    between: 'to',
};
export const virtualFieldToIndexField = {
    genre: 'genres.name',
    actor: 'actors.name',
    writer: 'writer.name',
    director: 'director.name',
    studio: 'studio.name',
    year: 'originalReleaseYear',
    release_date: 'originalReleaseDate',
    release_year: 'originalReleaseYear',
    // these get mapped to the duration field and their
    // values get converted to the appropriate units
    minutes: 'duration',
    seconds: 'duration',
    // This isn't really true, since this could map to multiple fields
    // TODO: Make grouping-tyhpe specific subdocs
    show_genre: 'grandparent.genres',
    show_title: 'grandparent.title',
    show_tags: 'grandparent.tags',
    show_studio: 'grandparent.studio',
    grandparent_genre: 'grandparent.genres',
    video_bit_depth: 'videoBitDepth',
    video_codec: 'videoCodec',
    video_height: 'videoHeight',
    video_width: 'videoWidth',
    audio_language: 'audioLanguages',
    subtitle_language: 'subtitleLanguages',
    audio_codec: 'audioCodec',
    audio_channels: 'audioChannels',
    // library_name: 'libraryName',
    // media_source_name: 'mediaSourceName'
};
export const indexFieldToVirtualField = invert(virtualFieldToIndexField, true);
const indexOperatorToSyntax = {
    contains: '~',
    'not contains': '!~',
    to: 'between',
};
function normalizeReleaseDate(value) {
    for (const format of ['YYYY-MM-DD', 'YYYYMMDD']) {
        const d = dayjs(value, format, true);
        if (d.isValid()) {
            return +d;
        }
    }
    throw new Error(`Could not parse inputted date string: ${value}`);
}
const numericFieldNormalizersByField = {
    minutes: (mins) => mins * 60 * 1000,
    seconds: (secs) => secs * 1000,
};
// Inverse converters for round-trip conversion back to original units
const numericFieldDenormalizersByField = {
    minutes: (ms) => ms / (60 * 1000),
    seconds: (ms) => ms / 1000,
};
const dateFieldNormalizersByField = {
    release_date: normalizeReleaseDate,
};
export class SearchParser extends EmbeddedActionsParser {
    constructor() {
        super(allTokens, { recoveryEnabled: false });
        this.performSelfAnalysis();
    }
    searchValue = this.RULE('searchValue', () => {
        const valueParts = [];
        return this.OR([
            {
                // Attempt to consume a quoted string.
                ALT: () => {
                    this.CONSUME(OpenQuote, { LABEL: 'str_open' });
                    this.AT_LEAST_ONE({
                        DEF: () => {
                            this.MANY(() => {
                                valueParts.push(this.CONSUME2(StringChars, { LABEL: 'query' }).image);
                            });
                        },
                    });
                    this.CONSUME3(CloseQuote, { LABEL: 'str_close' });
                    return valueParts.join(' ');
                },
            },
            {
                // Attempt to consume an unquoted string. Consumes both "integers" and "identifiers"
                // and joins them with empty string to complete a singular string. This handles things
                // like dates, e.g. 2025-03-02
                ALT: () => {
                    this.MANY2(() => {
                        this.OR3([
                            {
                                ALT: () => valueParts.push(this.CONSUME4(Identifier, { LABEL: 'query' }).image),
                            },
                            {
                                ALT: () => valueParts.push(this.CONSUME4(Integer, { LABEL: 'query' }).image),
                            },
                        ]);
                    });
                    return valueParts.join('');
                },
            },
        ]);
    });
    stringOperator = this.RULE('string_operator', () => {
        return this.OR([
            {
                ALT: () => {
                    const op = this.OR2([
                        {
                            ALT: () => {
                                const tok = this.CONSUME(EqOperator);
                                return tok.image === ':' ? '=' : tok.image;
                            },
                        },
                        {
                            ALT: () => this.CONSUME(NeqOperator).image,
                        },
                        {
                            ALT: () => this.CONSUME(LessThanOrEqualOperator).image,
                        },
                        {
                            ALT: () => this.CONSUME(LessThanOperator).image,
                        },
                        {
                            ALT: () => {
                                this.CONSUME(NotContainsOperator);
                                return 'not contains';
                            },
                        },
                        {
                            ALT: () => {
                                this.CONSUME(ContainsOperator);
                                return 'contains';
                            },
                        },
                    ]);
                    const value = this.SUBRULE(this.searchValue);
                    return {
                        op,
                        value,
                    };
                },
            },
            {
                ALT: () => {
                    let negate = false;
                    if (this.OPTION(() => this.CONSUME(NotOperator))) {
                        negate = true;
                    }
                    const op = this.CONSUME2(InOperator).image.toLowerCase();
                    this.CONSUME3(OpenArray);
                    const values = [];
                    this.AT_LEAST_ONE_SEP({
                        DEF: () => {
                            values.push(this.SUBRULE2(this.searchValue));
                        },
                        SEP: Comma,
                    });
                    this.CONSUME4(CloseArray);
                    // Safe because of "at least one" above
                    return { op, value: values, negate };
                },
            },
        ]);
    });
    numericOperator = this.RULE('numeric_operator', () => {
        return this.OR([
            {
                ALT: () => {
                    const op = this.OR2([
                        {
                            ALT: () => {
                                const tok = this.CONSUME(EqOperator);
                                return tok.image === ':' ? '=' : tok.image;
                            },
                        },
                        {
                            ALT: () => this.CONSUME(NeqOperator).image,
                        },
                        {
                            ALT: () => this.CONSUME(LessThanOrEqualOperator).image,
                        },
                        {
                            ALT: () => this.CONSUME(GreaterThanOrEqualOperator).image,
                        },
                        {
                            ALT: () => this.CONSUME(LessThanOperator).image,
                        },
                        {
                            ALT: () => this.CONSUME(GreaterThanOperator).image,
                        },
                    ]);
                    const value = this.SUBRULE(this.numericValue);
                    return {
                        op,
                        value,
                        type: 'single_numeric_query',
                    };
                },
            },
            {
                ALT: () => {
                    const op = this.CONSUME(BetweenOperator).image.toLowerCase();
                    let inclLow = false, inclHi = false;
                    this.OR3([
                        {
                            ALT: () => this.CONSUME2(OpenParenGroup),
                        },
                        {
                            ALT: () => {
                                this.CONSUME2(OpenArray);
                                inclLow = true;
                            },
                        },
                    ]);
                    const values = [];
                    values.push(this.SUBRULE2(this.numericValue));
                    this.OPTION(() => this.CONSUME2(Comma));
                    values.push(this.SUBRULE3(this.numericValue));
                    this.OR4([
                        {
                            ALT: () => this.CONSUME3(CloseParenGroup),
                        },
                        {
                            ALT: () => {
                                this.CONSUME3(CloseArray);
                                inclHi = true;
                            },
                        },
                    ]);
                    return {
                        op,
                        value: values,
                        includeHigher: inclHi,
                        includeLow: inclLow,
                        type: 'single_numeric_query',
                    };
                },
            },
        ]);
    });
    numericValue = this.RULE('numeric_value', () => this.OR([
        {
            ALT: () => parseFloat(this.CONSUME(FloatingPoint).image),
        },
        {
            ALT: () => parseInt(this.CONSUME(Integer).image),
        },
    ]));
    dateOperatorAndValue = this.RULE('date_operator', () => {
        return this.OR([
            {
                ALT: () => {
                    const op = this.OR2([
                        {
                            ALT: () => {
                                const tok = this.CONSUME(EqOperator);
                                return tok.image === ':' ? '=' : tok.image;
                            },
                        },
                        {
                            ALT: () => this.CONSUME(LessThanOrEqualOperator).image,
                        },
                        {
                            ALT: () => this.CONSUME(LessThanOperator).image,
                        },
                        {
                            ALT: () => this.CONSUME(GreaterThanOrEqualOperator).image,
                        },
                        {
                            ALT: () => this.CONSUME(GreaterThanOperator).image,
                        },
                    ]);
                    const value = this.SUBRULE(this.searchValue);
                    return {
                        type: 'single_date_query',
                        op,
                        value,
                    };
                },
            },
            {
                ALT: () => {
                    const op = this.CONSUME(BetweenOperator).image.toLowerCase();
                    let inclLow = false, inclHi = false;
                    this.OR3([
                        {
                            ALT: () => this.CONSUME2(OpenParenGroup),
                        },
                        {
                            ALT: () => {
                                this.CONSUME2(OpenArray);
                                inclLow = true;
                            },
                        },
                    ]);
                    const values = [];
                    values.push(this.SUBRULE2(this.searchValue));
                    this.OPTION(() => this.CONSUME2(Comma));
                    values.push(this.SUBRULE3(this.searchValue));
                    this.OR4([
                        {
                            ALT: () => this.CONSUME3(CloseParenGroup),
                        },
                        {
                            ALT: () => {
                                this.CONSUME3(CloseArray);
                                inclHi = true;
                            },
                        },
                    ]);
                    return {
                        op,
                        value: values,
                        includeHigher: inclHi,
                        includeLow: inclLow,
                        type: 'single_date_query',
                    };
                },
            },
        ]);
    });
    singleStringSearch = this.RULE('singleStringSearch', () => {
        const field = this.CONSUME(StringField, { LABEL: 'field' }).image;
        const { op, value, negate } = this.SUBRULE(this.stringOperator, {
            LABEL: 'op',
        });
        return {
            type: 'single_query',
            field,
            op,
            value,
            negate,
        };
    });
    singleNumericSearch = this.RULE('singleNumericSearch', () => {
        const field = this.CONSUME(NumericField).image;
        const opRet = this.SUBRULE(this.numericOperator);
        if (opRet.op === 'between') {
            return {
                type: 'single_numeric_query',
                field,
                op: 'between',
                value: opRet.value,
                includeHigher: opRet.includeHigher,
                includeLow: opRet.includeLow,
            };
        }
        return {
            type: 'single_numeric_query',
            field,
            op: opRet.op,
            value: opRet.value,
        };
    });
    singleDateSearch = this.RULE('singleDateSearch', () => {
        const field = this.CONSUME(DateField, { LABEL: 'field' }).image;
        const opRet = this.SUBRULE(this.dateOperatorAndValue, { LABEL: 'op' });
        if (opRet.op === 'between') {
            return {
                type: 'single_date_query',
                field,
                op: 'between',
                value: opRet.value,
                includeHigher: opRet.includeHigher,
                includeLow: opRet.includeLow,
            };
        }
        return {
            type: 'single_date_query',
            field,
            op: opRet.op,
            value: opRet.value,
        };
    });
    parenGroup = this.RULE('parenGroup', () => {
        this.CONSUME(OpenParenGroup);
        const clauses = [];
        this.AT_LEAST_ONE(() => {
            clauses.push(this.SUBRULE(this.searchClause));
        });
        this.CONSUME(CloseParenGroup);
        return {
            type: 'search_group',
            clauses,
        };
    });
    singleSearch = this.RULE('singleSearch', () => {
        return this.OR([
            {
                ALT: () => this.SUBRULE(this.singleStringSearch),
            },
            {
                ALT: () => this.SUBRULE(this.singleNumericSearch),
            },
            {
                ALT: () => this.SUBRULE(this.singleDateSearch),
            },
        ]);
    });
    searchClause = this.RULE('searchClause', () => {
        let clause;
        // First, match either a parenGroup or a singleSearch
        this.OR([
            {
                ALT: () => {
                    clause = this.SUBRULE(this.parenGroup);
                },
            },
            {
                ALT: () => {
                    clause = this.SUBRULE(this.singleSearch);
                },
            },
        ]);
        // Then, optionally check for a binary operator (AND/OR)
        let isCombo = false;
        let rhs;
        let op = 'and';
        this.OPTION(() => {
            const opToken = this.OR2([
                {
                    ALT: () => this.CONSUME(CombineOr),
                },
                {
                    ALT: () => this.CONSUME(CombineAnd),
                },
                {
                    ALT: () => this.CONSUME(WhiteSpace),
                },
            ]);
            rhs = this.SUBRULE(this.searchClause);
            op = opToken?.tokenType?.name === CombineOr.name ? 'or' : 'and';
            isCombo = true;
        });
        if (isCombo) {
            clause = {
                type: 'binary_clause',
                lhs: clause,
                op,
                rhs: rhs,
            };
        }
        return clause;
    });
    searchExpression = this.RULE('searchExpression', () => {
        const clauses = [];
        this.AT_LEAST_ONE(() => {
            clauses.push(this.SUBRULE(this.searchClause));
        });
        return clauses.reduce((prev, value) => {
            return {
                lhs: prev,
                op: 'and',
                rhs: value,
                type: 'binary_clause',
            };
        });
    });
}
export function tokenizeSearchQuery(queryString) {
    return SearchExpressionLexer.tokenize(queryString);
}
// Helper to flatten chains of same-operator binary clauses
// e.g., binary_clause(A, and, binary_clause(B, and, C)) -> [A, B, C]
function collectBinaryClauseChildren(clause, op) {
    if (clause.type === 'binary_clause' && clause.op === op) {
        return [
            ...collectBinaryClauseChildren(clause.lhs, op),
            ...collectBinaryClauseChildren(clause.rhs, op),
        ];
    }
    return [clause];
}
// Parse a valid SearchClause into an actual Tunarr SearchFilter
export function parsedSearchToRequest(input, normalizeValues = true) {
    switch (input.type) {
        case 'search_group': {
            if (input.clauses.length === 1) {
                const innerClause = input.clauses[0];
                // If the inner clause is a binary clause, flatten it and mark as grouped
                if (innerClause.type === 'binary_clause') {
                    const flatChildren = collectBinaryClauseChildren(innerClause, innerClause.op);
                    return {
                        op: innerClause.op,
                        type: 'op',
                        grouped: true,
                        children: flatChildren.map((child) => parsedSearchToRequest(child, normalizeValues)),
                    };
                }
                // Single non-binary clause doesn't need grouping
                return parsedSearchToRequest(innerClause);
            }
            return {
                op: 'and',
                type: 'op',
                grouped: true,
                children: input.clauses.map((child) => parsedSearchToRequest(child, normalizeValues)),
            };
        }
        case 'single_numeric_query': {
            const key = virtualFieldToIndexField[input.field] ?? input.field;
            // Store original field name for round-trip conversion
            const originalField = input.field;
            const valueConverter = input.field in numericFieldNormalizersByField
                ? numericFieldNormalizersByField[input.field]
                : identity;
            // Anything other than full inclusive needs to be translated
            // to a binary query.
            if (input.op === 'between') {
                return match([input.includeLow, input.includeHigher])
                    .returnType()
                    .with([true, true], () => ({
                    type: 'value',
                    fieldSpec: {
                        key,
                        name: originalField,
                        op: NumericOpToApiType[input.op],
                        type: 'numeric',
                        value: [
                            valueConverter(input.value[0]),
                            valueConverter(input.value[1]),
                        ],
                    },
                }))
                    .otherwise(([inclLow, inclHi]) => {
                    const lhs = {
                        type: 'value',
                        fieldSpec: {
                            key,
                            name: originalField,
                            op: inclLow ? '>=' : '>',
                            type: 'numeric',
                            value: valueConverter(input.value[0]),
                        },
                    };
                    const rhs = {
                        type: 'value',
                        fieldSpec: {
                            key,
                            name: originalField,
                            op: inclHi ? '<=' : '<',
                            type: 'numeric',
                            value: valueConverter(input.value[1]),
                        },
                    };
                    return {
                        type: 'op',
                        op: 'and',
                        children: [lhs, rhs],
                    };
                });
            }
            return {
                type: 'value',
                fieldSpec: {
                    key,
                    name: originalField,
                    op: NumericOpToApiType[input.op],
                    type: 'numeric',
                    value: valueConverter(input.value),
                },
            };
        }
        case 'single_date_query': {
            const originalField = input.field;
            const key = virtualFieldToIndexField[input.field] ?? input.field;
            const converter = input.field in dateFieldNormalizersByField
                ? dateFieldNormalizersByField[input.field]
                : (input) => parseInt(input);
            if (input.op === 'between') {
                return {
                    type: 'value',
                    fieldSpec: {
                        key,
                        name: originalField,
                        op: NumericOpToApiType[input.op],
                        type: 'date',
                        value: [converter(input.value[0]), converter(input.value[1])],
                    },
                };
            }
            else {
                return {
                    type: 'value',
                    fieldSpec: {
                        key,
                        name: originalField,
                        op: NumericOpToApiType[input.op],
                        type: 'date',
                        value: converter(input.value),
                    },
                };
            }
        }
        case 'single_query': {
            const key = virtualFieldToIndexField[input.field] ?? input.field;
            const op = match(input)
                .returnType()
                .with({ op: 'in', negate: true }, () => 'not in')
                .otherwise(() => StringOpToApiType[input.op]);
            return {
                type: 'value',
                fieldSpec: {
                    key,
                    name: input.field,
                    op,
                    type: inConstArr(FactedStringFields, input.field)
                        ? 'faceted_string'
                        : 'string',
                    value: isArray(input.value) ? input.value : [input.value],
                },
            };
        }
        case 'binary_clause': {
            // Flatten chains of same-operator binary clauses
            const flatChildren = collectBinaryClauseChildren(input, input.op);
            return {
                op: input.op.toLowerCase(),
                type: 'op',
                children: flatChildren.map((child) => parsedSearchToRequest(child, normalizeValues)),
            };
        }
    }
}
// Applies denormalizers to relevant fields. Useful for UI
export function denormalizeSearchFilter(input) {
    return match(input)
        .returnType()
        .with({ type: 'op', children: [P.select()] }, (child) => denormalizeSearchFilter(child))
        .with({ type: 'op' }, (op) => ({
        ...op,
        children: op.children.map(denormalizeSearchFilter),
    }))
        .with({
        type: 'value',
        fieldSpec: { type: 'numeric', name: P.when(isNonEmptyString) },
    }, (numeric) => {
        const denorm = has(numericFieldDenormalizersByField, numeric.fieldSpec.name)
            ? numericFieldDenormalizersByField[numeric.fieldSpec.name]
            : identity;
        if (isArray(numeric.fieldSpec.value)) {
            return {
                type: 'value',
                fieldSpec: {
                    ...numeric.fieldSpec,
                    value: numeric.fieldSpec.value.map(denorm),
                },
            };
        }
        else {
            return {
                type: 'value',
                fieldSpec: {
                    ...numeric.fieldSpec,
                    value: denorm(numeric.fieldSpec.value),
                },
            };
        }
    })
        .with({ type: 'value' }, (field) => field)
        .exhaustive();
}
/**
 * Takes a search filter which may have virtual fields and / or need value
 * conversions and normalizes it to prepare it for search against the Tunarr
 * API
 */
export function normalizeSearchFilter(input) {
    return match(input)
        .returnType()
        .with({ type: 'op', children: [P.select()] }, (child) => normalizeSearchFilter(child))
        .with({ type: 'op' }, (op) => ({
        ...op,
        children: op.children.map(normalizeSearchFilter),
    }))
        .with({ type: 'value', fieldSpec: { type: 'numeric' } }, (numeric) => {
        const aliasOrKey = numeric.fieldSpec.name ?? numeric.fieldSpec.key;
        const key = (isNonEmptyString(numeric.fieldSpec.name)
            ? virtualFieldToIndexField[numeric.fieldSpec.name]
            : null) ?? numeric.fieldSpec.key;
        const valueConverter = has(numericFieldNormalizersByField, aliasOrKey)
            ? numericFieldNormalizersByField[aliasOrKey]
            : identity;
        if (isArray(numeric.fieldSpec.value)) {
            return {
                type: 'value',
                fieldSpec: {
                    ...numeric.fieldSpec,
                    key,
                    value: [
                        valueConverter(numeric.fieldSpec.value[0]),
                        valueConverter(numeric.fieldSpec.value[1]),
                    ],
                },
            };
        }
        else {
            return {
                type: 'value',
                fieldSpec: {
                    ...numeric.fieldSpec,
                    key,
                    value: valueConverter(numeric.fieldSpec.value),
                },
            };
        }
    })
        .with({ type: 'value', fieldSpec: { type: 'date' } }, ({ fieldSpec }) => {
        const key = virtualFieldToIndexField[fieldSpec.key] ?? fieldSpec.key;
        const converter = identity;
        if (isArray(fieldSpec.value)) {
            return {
                type: 'value',
                fieldSpec: {
                    ...fieldSpec,
                    key,
                    value: [
                        converter(fieldSpec.value[0]),
                        converter(fieldSpec.value[1]),
                    ],
                },
            };
        }
        else {
            return {
                type: 'value',
                fieldSpec: {
                    ...fieldSpec,
                    key,
                    value: converter(fieldSpec.value),
                },
            };
        }
    })
        .with({
        type: 'value',
        fieldSpec: { type: P.union('faceted_string', 'string') },
    }, ({ fieldSpec }) => {
        const key = virtualFieldToIndexField[fieldSpec.key] ?? fieldSpec.key;
        return {
            type: 'value',
            fieldSpec: {
                ...fieldSpec,
                key,
                type: 'string',
            },
        };
    })
        .exhaustive();
}
export function searchFilterToString(input) {
    switch (input.type) {
        case 'op': {
            const children = input.children.map((child) => searchFilterToString(child));
            if (children.length === 0) {
                return '';
            }
            if (children.length === 1) {
                return children[0];
            }
            const joined = children.join(` ${input.op.toUpperCase()} `);
            // Only wrap in parentheses if this was an explicit group in the original query
            return input.grouped ? `(${joined})` : joined;
        }
        case 'value': {
            // Use the original field name if stored, otherwise fall back to index field mapping
            const key = emptyStringToNull(input.fieldSpec.name) ??
                head(indexFieldToVirtualField[input.fieldSpec.key]) ??
                input.fieldSpec.key;
            const op = indexOperatorToSyntax[input.fieldSpec.op] ?? input.fieldSpec.op;
            // Get denormalizer for numeric fields (e.g., convert ms back to minutes)
            const denormalizer = input.fieldSpec.type === 'numeric' &&
                isNonEmptyString(input.fieldSpec.name) &&
                has(numericFieldDenormalizersByField, input.fieldSpec.name)
                ? numericFieldDenormalizersByField[input.fieldSpec.name]
                : identity;
            let valueString;
            if (isNumber(input.fieldSpec.value)) {
                valueString =
                    input.fieldSpec.type === 'date'
                        ? dayjs(input.fieldSpec.value).format('YYYY-MM-DD')
                        : denormalizer(input.fieldSpec.value).toString();
            }
            else if (input.fieldSpec.value.length === 0) {
                return '';
            }
            else if (input.fieldSpec.value.length === 1 &&
                input.fieldSpec.op !== 'in' &&
                input.fieldSpec.op !== 'not in') {
                const value = input.fieldSpec.value[0];
                const repr = `"${value}"`;
                return `${key} ${op} ${repr}`;
            }
            else {
                const components = [];
                for (const x of input.fieldSpec.value) {
                    if (isNumber(x)) {
                        components.push(denormalizer(x).toString());
                    }
                    else {
                        components.push(`"${x}"`);
                    }
                }
                valueString = `[${components.join(', ')}]`;
            }
            return `${key} ${op} ${valueString}`;
        }
    }
}
export function searchItemTypeFromContentType(mediaType) {
    switch (mediaType) {
        case 'movies':
            return 'movie';
        case 'shows':
            return 'show';
        case 'tracks':
            return 'artist';
        case 'other_videos':
            return 'other_video';
        case 'music_videos':
            return 'music_video';
    }
}
export function makeSearchTypeFilter(mediaType) {
    return {
        type: 'value',
        fieldSpec: {
            key: 'type',
            name: 'Type',
            op: '=',
            type: 'string',
            value: [searchItemTypeFromContentType(mediaType)],
        },
    };
}
//# sourceMappingURL=searchUtil.js.map